'use client'

import {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useMemo
} from 'react'

import type { MissionProgress } from '@/features/missions/types/mission'
import type { PassObjectiveProgressState } from '@/features/passes/types/pass'
import {
  useOptimizedRecord,
  useOptimizedRecords
} from '@/shared/hooks/useOptimizedQuery'
import { API_PATHS } from '@/shared/lib/data/paths'
import type {
  CampaignLink,
  CampaignParticipant
} from '@/shared/types/campaign'
import type { ProgressResponse } from '@/shared/types/user-progress'

interface UserProgressContextValue {
  progress: ProgressResponse | null
  refetchProgress: () => Promise<unknown>

  journeyWasFinished: (journeyId: number) => boolean
  stageWasFinished: (stageId: number) => boolean
  levelWasFinished: (levelId: number) => boolean
  documentWasFinished: (documentId: number) => boolean

  wallet: {
    points: number
    coins: number
  }
  title: string | null

  missionProgresses: MissionProgress[]
  refetchMissionProgresses: () => Promise<unknown>

  passObjectiveProgresses: PassObjectiveProgressState[]
  campaignIds: number[]
  campaignParticipants: CampaignParticipant[]
  campaignLinks: CampaignLink[]
}

const EMPTY_ARRAY: number[] = []
const EMPTY_PARTICIPANTS: CampaignParticipant[] = []
const EMPTY_LINKS: CampaignLink[] = []

const UserProgressContext =
  createContext<UserProgressContextValue | null>(null)

export function useUserProgress(): UserProgressContextValue {
  const ctx = useContext(UserProgressContext)
  if (!ctx) {
    throw new Error(
      'useUserProgress must be used within a UserProgressProvider'
    )
  }
  return ctx
}

interface UserProgressProviderProps {
  children: ReactNode
  initialData?: ProgressResponse | null
}

export function UserProgressProvider({
  children,
  initialData
}: UserProgressProviderProps) {
  const { record: fetchedProgress, refetch: refetchProgress } =
    useOptimizedRecord<ProgressResponse>({
      path: API_PATHS.USER_PROGRESS
    })

  const progress = fetchedProgress ?? initialData ?? null

  /* ------------------ Derived primitive state ------------------ */

  const title = progress?.title ?? null

  const wallet = useMemo(
    () => ({
      points: progress?.wallet?.points ?? 0,
      coins: progress?.wallet?.coins ?? 0
    }),
    [progress?.wallet?.points, progress?.wallet?.coins]
  )

  /* ------------------ Normalized progress (Sets) ------------------ */

  const finishedJourneysSet = useMemo(
    () => new Set(progress?.finished_journeys ?? EMPTY_ARRAY),
    [progress?.finished_journeys]
  )

  const finishedStagesSet = useMemo(
    () => new Set(progress?.finished_stages ?? EMPTY_ARRAY),
    [progress?.finished_stages]
  )

  const finishedLevelsSet = useMemo(
    () => new Set(progress?.finished_levels ?? EMPTY_ARRAY),
    [progress?.finished_levels]
  )

  const finishedDocumentsSet = useMemo(
    () => new Set(progress?.finished_documents ?? EMPTY_ARRAY),
    [progress?.finished_documents]
  )

  /* ------------------ Stable helpers ------------------ */

  const journeyWasFinished = useCallback(
    (journeyId: number) => finishedJourneysSet.has(journeyId),
    [finishedJourneysSet]
  )

  const stageWasFinished = useCallback(
    (stageId: number) => finishedStagesSet.has(stageId),
    [finishedStagesSet]
  )

  const levelWasFinished = useCallback(
    (levelId: number) => finishedLevelsSet.has(levelId),
    [finishedLevelsSet]
  )

  const documentWasFinished = useCallback(
    (documentId: number) => finishedDocumentsSet.has(documentId),
    [finishedDocumentsSet]
  )

  /* ------------------ Missions ------------------ */

  const {
    records: missionProgresses = [],
    refetch: refetchMissionProgresses
  } = useOptimizedRecords<MissionProgress>({
    path: API_PATHS.USER_MISSIONS_PROGRESSES
  })

  /* ------------------ Passes ------------------ */

  const passObjectiveProgresses = useMemo(
    () => progress?.pass_objective_progresses ?? [],
    [progress?.pass_objective_progresses]
  )

  const campaignIds = useMemo(
    () => progress?.campaign_ids ?? EMPTY_ARRAY,
    [progress?.campaign_ids]
  )

  const campaignParticipants = useMemo(
    () => progress?.campaign_participants ?? EMPTY_PARTICIPANTS,
    [progress?.campaign_participants]
  )

  const campaignLinks = useMemo(
    () => progress?.campaign_links ?? EMPTY_LINKS,
    [progress?.campaign_links]
  )

  /* ------------------ Context value ------------------ */

  const contextValue = useMemo<UserProgressContextValue>(
    () => ({
      progress,
      refetchProgress,

      journeyWasFinished,
      stageWasFinished,
      levelWasFinished,
      documentWasFinished,

      wallet,
      title,

      missionProgresses,
      refetchMissionProgresses,

      passObjectiveProgresses,
      campaignIds,
      campaignParticipants,
      campaignLinks
    }),
    [
      progress,
      refetchProgress,
      journeyWasFinished,
      stageWasFinished,
      levelWasFinished,
      documentWasFinished,
      wallet,
      title,
      missionProgresses,
      refetchMissionProgresses,
      passObjectiveProgresses,
      campaignIds,
      campaignParticipants,
      campaignLinks
    ]
  )

  return (
    <UserProgressContext.Provider value={contextValue}>
      {children}
    </UserProgressContext.Provider>
  )
}
